import React from 'react';
import moment from 'moment-timezone';
import { once, uniqWith } from 'lodash';
import { ReactStripeElements, injectStripe } from 'react-stripe-elements';

import CoachingIsolatedViewHeader from 'coaching/components/CoachingIsolatedViewHeader';
import CoachingSelfServeFailureView from './CoachingSelfServeFailureView';
import CoachingSelfServePaymentView from './CoachingSelfServePaymentView';
import CoachingSelfServeScheduleSessionView from './CoachingSelfServeScheduleSessionView';
import CoachingSelfServeStudentDetailsView from './CoachingSelfServeStudentDetailsView';
import CoachingSelfServePostPurchaseView from './CoachingSelfServePostPurchaseView';
import CoachingSelfServeSuccessView from './CoachingSelfServeSuccessView';
import Container from 'components/Container';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import GetAvailableReservationsContainer, {
  CoachingReservation,
} from 'coaching/containers/GetAvailableReservationsContainer';
import GetProspectiveStudentOptions, {
  Student,
} from 'coaching/containers/GetProspectiveStudentOptionsContainer';
import { getNextNCoachingDays, getDatesFromReservations } from 'coaching/lib/timeIntervals';
import Icon from 'components/Icon';
import Link from 'components/Link';
import { purchaseCoaching } from 'coaching/actions/CoachingActions';
import Row from 'components/Row';
import TimezoneHelper from 'lib/timezones';
import ToastActions from 'actions/ToastActions';
import CoachingSelfServeIntroView from 'coaching/components/CoachingSelfServeView/CoachingSelfServeIntroView';

import '../styles/CoachingIsolatedView.scss';
import '../styles/CoachingSelfServeView.scss';

// exporting Props to add types to the unit test
export type Props = {};

type State = {
  currentStep: number;
  selectedSubject?: string;
  selectedStudent?: Student;
  selectedGradeLevel?: string;
  manualInputChildName?: string;
  selectedReservation?: CoachingReservation;
  weekOffset: number;
  userTimezone: string;
  cardInputCompleted: boolean;
  submittingPayment: boolean;

  // this is the UUID of the participant row in r101-coaching
  // which gets created *after* a successful purchase
  participantUUID?: string;
};

class CoachingSelfServeView extends React.Component<
  Props & ReactStripeElements.InjectedStripeProps,
  State
> {
  constructor(props: Props) {
    super(props);

    this.state = {
      currentStep: 1,
      weekOffset: 0,
      userTimezone: TimezoneHelper.userTimezoneDefaultValue ?? 'America/Los_Angeles',
      cardInputCompleted: false,
      submittingPayment: false,
    };
  }

  // lock the current date/time to anchor the availability queries
  currentDate = moment().toISOString();

  showStep = (step: number) => {
    this.setState({ currentStep: step });
  };

  getSelectedStudentInfo = (): (Partial<Student> & { name: string }) | undefined => {
    const { selectedStudent, manualInputChildName } = this.state;
    if (!selectedStudent && !manualInputChildName) {
      return undefined;
    }
    return (
      selectedStudent ?? {
        name: manualInputChildName!,
      }
    );
  };

  handleSelectReservation = (reservation: CoachingReservation) => {
    this.setState({ selectedReservation: reservation });
  };

  handleSelectStudent = (studentUuid: string, students?: Student[]) => {
    const student = students?.find(s => s.uuid === studentUuid);
    if (!student) {
      ToastActions.showErrorToast(
        'Oops - looks like something went wrong. Please try again or select a different student.'
      );
      console.error('CoachingSelfServeView - tried to select student that does not exist');
      return;
    }
    this.setState({ selectedStudent: student, manualInputChildName: undefined });
  };

  handleManuallyInputChildName = (childName?: string) => {
    this.setState({ selectedStudent: undefined, manualInputChildName: childName });
  };

  handleSubmitPayment = once(async () => {
    const user = CurrentUser.getUser();
    this.setState({ submittingPayment: true });
    const { token, error } = await this.props.stripe!.createToken({
      name: user.getDisplayName(),
    });
    if (!token) {
      ToastActions.showErrorToast(
        error?.message ?? 'There was a problem saving your card. Please try again.'
      );
      console.warn(error?.message);
      console.error('CoachingSelfServeView - problem tokenizing card');
      this.setState({ submittingPayment: false });
      return;
    }
    const { selectedReservation, userTimezone, selectedSubject, selectedGradeLevel } = this.state;
    const selectedStudent = this.getSelectedStudentInfo();
    if (!selectedReservation || !selectedSubject || !selectedStudent || !selectedGradeLevel) {
      ToastActions.showErrorToast(
        'Looks like you are missing some information. Please go back and fill out all of the fields before submitting payment.'
      );
      console.error('CoachingSelfServeView - tried to submit without a reservation or subject');
      this.setState({ submittingPayment: false });
      return;
    }
    return purchaseCoaching(
      selectedReservation.startDateTime.toISOString(),
      selectedReservation.endDateTime.toISOString(),
      selectedReservation.coachUuid,
      token.id,
      user.getUuid(),
      userTimezone,
      [selectedSubject],
      selectedStudent,
      selectedGradeLevel
    ).then(res => {
      if (res.participantUuid) {
        this.setState({ submittingPayment: false, participantUUID: res.participantUuid });
        this.showStep(4);
      } else {
        this.setState({ submittingPayment: false });
        console.error('CoachingSelfServeView - purchase failed');
        this.showStep(5);
      }
    });
  });

  childSelectionStepIsInvalid = () => {
    return (
      !this.getSelectedStudentInfo() ||
      !this.state.selectedSubject ||
      !this.state.selectedGradeLevel
    );
  };

  scheduleSessionStepIsInvalid = () => {
    return !this.state.selectedReservation;
  };

  renderGetStartedStep = () => {
    return <CoachingSelfServeIntroView onContinue={() => this.showStep(1)} />;
  };

  renderChildSelectionStep = () => {
    //container here
    return (
      <GetProspectiveStudentOptions>
        {({ students, subjects, gradeLevels, loading, error }) => (
          <CoachingSelfServeStudentDetailsView
            students={students}
            subjects={subjects}
            gradeLevels={gradeLevels}
            loading={loading}
            loadingError={error?.message}
            selectedSubject={this.state.selectedSubject}
            selectedStudentUuid={this.state.selectedStudent?.uuid}
            onSelectSubject={subject => this.setState({ selectedSubject: subject })}
            onSelectStudent={uuid => this.handleSelectStudent(uuid, students)}
            selectedGradeLevel={this.state.selectedGradeLevel}
            onSelectGradeLevel={gradeLevel => this.setState({ selectedGradeLevel: gradeLevel })}
            onChangeName={this.handleManuallyInputChildName}
            onContinue={() => this.showStep(2)}
            disabledContinueButton={this.childSelectionStepIsInvalid()}
            manualInputChildName={this.state.manualInputChildName}
          />
        )}
      </GetProspectiveStudentOptions>
    );
  };

  renderScheduleSessionStep = () => {
    const { selectedSubject, weekOffset, userTimezone } = this.state;
    if (!selectedSubject) {
      return (
        <ErrorReporter error="CoachingSelfServeView - tried to render schedule session without a subject">
          Oops! Looks like you haven't chosen a math subject. Please go back and select one.
        </ErrorReporter>
      );
    }
    const dateTimeRange = getNextNCoachingDays(6, weekOffset, userTimezone, this.currentDate);
    const startDateTimeRange = dateTimeRange[0]?.toISOString();
    const endDateTimeRange = dateTimeRange
      .slice(-1)
      .pop()
      ?.toISOString();
    if (!startDateTimeRange || !endDateTimeRange) {
      return (
        <ErrorReporter error="CoachingSelfServeView - tried to render schedule session without a datetime range">
          Oops! Looks like something went wrong. Please try refreshing the page.
        </ErrorReporter>
      );
    }

    return (
      <GetAvailableReservationsContainer
        subject={selectedSubject}
        startDateTimeRange={startDateTimeRange}
        endDateTimeRange={endDateTimeRange}>
        {({ reservations, timeOff, error, loading }) => {
          // We may get available reservation slots on non-consecutive days with gaps between reservations that
          // can span week due to holidays, so just get the dates the reservations are on so that the UI can show
          // all the dates the reservations are on.
          const daysToShow = getDatesFromReservations(
            reservations?.map(x => x.startDateTime) ?? dateTimeRange
          );

          return (
            <CoachingSelfServeScheduleSessionView
              selectedSubject={selectedSubject}
              userTimezone={this.state.userTimezone}
              onSelectTimezone={tz => this.setState({ userTimezone: tz })}
              selectedReservation={this.state.selectedReservation}
              onSelectReservation={this.handleSelectReservation}
              onContinue={() => this.showStep(3)}
              disabledContinueButton={this.scheduleSessionStepIsInvalid()}
              daysToShow={daysToShow}
              reservations={reservations}
              timeOff={timeOff}
              loading={loading}
              error={error?.message}
            />
          );
        }}
      </GetAvailableReservationsContainer>
    );
  };

  renderPaymentStep = () => {
    const {
      manualInputChildName,
      selectedStudent,
      selectedReservation,
      userTimezone,
      cardInputCompleted,
    } = this.state;
    const childName = selectedStudent?.name ?? manualInputChildName;
    return (
      <CoachingSelfServePaymentView
        onCardInputCompleted={() => this.setState({ cardInputCompleted: true })}
        childName={childName}
        purchaseForConnectedChild={selectedStudent ? true : false}
        reservation={selectedReservation}
        userTimezone={userTimezone}
        disabledContinueButton={!cardInputCompleted}
        onSubmitPayment={this.handleSubmitPayment}
        submittingPayment={this.state.submittingPayment}
      />
    );
  };

  renderSuccess = () => {
    const user = CurrentUser.getUser();
    const student = this.getSelectedStudentInfo();
    const hasPostPurchaseFlowFlag = user.hasFeature('coaching_connect_student_account.web.nov-20');
    const purchasingForUnconnectedStudent = !student?.uuid;
    const Component =
      hasPostPurchaseFlowFlag && purchasingForUnconnectedStudent
        ? CoachingSelfServePostPurchaseView
        : CoachingSelfServeSuccessView;
    return (
      <Component
        childName={student?.name}
        reservation={this.state.selectedReservation}
        userTimezone={this.state.userTimezone}
        userEmail={user.getEmail()}
        childEmail={student?.email}
        participantUUID={this.state.participantUUID}
      />
    );
  };

  renderCurrentStep = () => {
    const mapStepToComponent: { [key: number]: () => React.ReactNode } = {
      0: this.renderGetStartedStep,
      1: this.renderChildSelectionStep,
      2: this.renderScheduleSessionStep,
      3: this.renderPaymentStep,
      4: this.renderSuccess,
      5: () => <CoachingSelfServeFailureView />,
    };

    return mapStepToComponent[this.state.currentStep]();
  };

  renderBreadcrumbs = () => {
    const chevron = <Icon size="small" name="chevron-right" color="eclipse" />;
    const childSelectionStepIsInvalid = this.childSelectionStepIsInvalid();
    const scheduleSessionStepIsInvalid = this.scheduleSessionStepIsInvalid();
    return (
      <Row>
        <div className="CoachingIsolatedView-breadcrumbs">
          {this.renderCrumb('Get started', () => this.showStep(0), 0)}
          {chevron}
          {this.renderCrumb('Details', () => this.showStep(1), 1)}
          {chevron}
          {this.renderCrumb(
            'Schedule',
            () => this.showStep(2),
            2,
            () => childSelectionStepIsInvalid
          )}
          {chevron}
          {this.renderCrumb(
            'Review',
            () => this.showStep(3),
            3,
            () => childSelectionStepIsInvalid || scheduleSessionStepIsInvalid
          )}
        </div>
      </Row>
    );
  };

  renderCrumb = (
    name: string,
    onClick: () => void,
    stepIndex: number,
    disabledCondition?: () => boolean
  ) => {
    const textColor = this.state.currentStep === stepIndex ? 'ink' : 'eclipse';
    return (
      <Link
        color={textColor}
        onClick={onClick}
        disabled={disabledCondition ? disabledCondition() : false}>
        {name}
      </Link>
    );
  };

  render() {
    const { currentStep } = this.state;
    return (
      <div className="CoachingIsolatedView">
        <CoachingIsolatedViewHeader />
        <Container size="medium">
          {currentStep < 4 ? this.renderBreadcrumbs() : <Row size="xxxxxlarge"> </Row>}
          {this.renderCurrentStep()}
        </Container>
      </div>
    );
  }
}

export { CoachingSelfServeView };

export default injectStripe(CoachingSelfServeView);
